import { Button, DatePicker, Input, InputNumber, Select, Space, Tag } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import type { FieldDefinitionDto, FieldOption, RuleCondition, RuleGroup } from '@crm/shared';
import type { PipelineItem } from '../features/audience/api';
import { fmtDate } from '../utils/format';

export interface AudienceOptions {
  tags?: { id: string; name: string }[];
  lists?: { id: string; name: string }[];
  segments?: { id: string; name: string }[];
  pipelines?: PipelineItem[];
}

type Kind = RuleCondition['kind'];
type ValueKind = 'text' | 'number' | 'date' | 'boolean' | 'select' | 'multiselect';

const OPS: Record<ValueKind, { value: string; label: string }[]> = {
  text: [
    { value: 'contains', label: 'contains' },
    { value: 'not_contains', label: 'does not contain' },
    { value: 'eq', label: 'is' },
    { value: 'neq', label: 'is not' },
    { value: 'starts_with', label: 'starts with' },
    { value: 'in', label: 'is any of' },
    { value: 'is_empty', label: 'is empty' },
    { value: 'is_not_empty', label: 'is not empty' },
  ],
  number: [
    { value: 'eq', label: '=' },
    { value: 'neq', label: '≠' },
    { value: 'gt', label: '>' },
    { value: 'gte', label: '≥' },
    { value: 'lt', label: '<' },
    { value: 'lte', label: '≤' },
    { value: 'is_empty', label: 'is empty' },
    { value: 'is_not_empty', label: 'is not empty' },
  ],
  date: [
    { value: 'within_days', label: 'in the last N days' },
    { value: 'not_within_days', label: 'not in the last N days' },
    { value: 'before', label: 'before' },
    { value: 'after', label: 'after' },
    { value: 'is_empty', label: 'is empty' },
    { value: 'is_not_empty', label: 'is not empty' },
  ],
  boolean: [{ value: 'eq', label: 'is' }],
  select: [
    { value: 'eq', label: 'is' },
    { value: 'neq', label: 'is not' },
    { value: 'in', label: 'is any of' },
    { value: 'not_in', label: 'is none of' },
    { value: 'is_empty', label: 'is empty' },
    { value: 'is_not_empty', label: 'is not empty' },
  ],
  multiselect: [
    { value: 'contains', label: 'includes any of' },
    { value: 'not_contains', label: 'includes none of' },
    { value: 'is_empty', label: 'is empty' },
    { value: 'is_not_empty', label: 'is not empty' },
  ],
};

const KIND_LABELS: Record<Kind, string> = {
  field: 'Contact field',
  organization: 'Organization field',
  tag: 'Tag',
  list: 'List',
  segment: 'Segment',
  deal: 'Deal',
  engagement: 'Email engagement',
  project: 'Project',
};

function valueKind(def: FieldDefinitionDto | undefined): ValueKind {
  switch (def?.type) {
    case 'NUMBER':
    case 'CURRENCY':
      return 'number';
    case 'DATE':
    case 'DATETIME':
      return 'date';
    case 'BOOLEAN':
      return 'boolean';
    case 'SELECT':
      return 'select';
    case 'MULTISELECT':
    case 'LIST':
      return 'multiselect';
    default:
      return 'text';
  }
}

interface Props {
  value: RuleGroup;
  onChange: (g: RuleGroup) => void;
  /** Fields of the object being filtered (contacts, or organizations when objectType is ORGANIZATION). */
  contactFields: FieldDefinitionDto[];
  organizationFields?: FieldDefinitionDto[];
  audience?: AudienceOptions;
  objectType?: 'CONTACT' | 'ORGANIZATION';
}

const sel = { size: 'small' as const, style: { minWidth: 160 } };

/** Flat rule builder (one level, all/any) producing the shared RuleGroup JSON. */
export function FilterBuilder({ value, onChange, contactFields, organizationFields = [], audience = {}, objectType = 'CONTACT' }: Props) {
  const conditions = value.conditions.filter((c): c is RuleCondition => 'kind' in c);
  const kinds: Kind[] =
    objectType === 'ORGANIZATION'
      ? ['field']
      : (['field', organizationFields.length ? 'organization' : null, audience.tags?.length ? 'tag' : null, audience.lists?.length ? 'list' : null, audience.segments?.length ? 'segment' : null, audience.pipelines?.length ? 'deal' : null, 'engagement'].filter(Boolean) as Kind[]);

  const set = (i: number, next: RuleCondition) => onChange({ ...value, conditions: conditions.map((c, idx) => (idx === i ? next : c)) });
  const remove = (i: number) => onChange({ ...value, conditions: conditions.filter((_, idx) => idx !== i) });
  const add = () => onChange({ ...value, conditions: [...conditions, defaultCondition('field', contactFields, organizationFields, audience)] });

  return (
    <div style={{ minWidth: 420, maxWidth: 'calc(100vw - 320px)' }}>
      <Space style={{ marginBottom: 8 }}>
        <span>Match</span>
        <Select size="small" value={value.combinator} onChange={(combinator) => onChange({ ...value, combinator })} options={[{ value: 'and', label: 'all' }, { value: 'or', label: 'any' }]} style={{ width: 80 }} />
        <span>of the conditions</span>
      </Space>
      {conditions.map((c, i) => (
        <Space key={i} align="start" style={{ display: 'flex', marginBottom: 8 }} wrap>
          {kinds.length > 1 && (
            <Select
              size="small"
              value={c.kind}
              style={{ width: 150 }}
              onChange={(kind: Kind) => set(i, defaultCondition(kind, contactFields, organizationFields, audience))}
              options={kinds.map((k) => ({ value: k, label: KIND_LABELS[k] }))}
            />
          )}
          <ConditionEditor c={c} onChange={(next) => set(i, next)} contactFields={contactFields} organizationFields={organizationFields} audience={audience} />
          <Button size="small" type="text" icon={<DeleteOutlined />} onClick={() => remove(i)} aria-label="Remove condition" title="Remove condition" />
        </Space>
      ))}
      <div>
        <Button size="small" icon={<PlusOutlined />} onClick={add}>
          Add condition
        </Button>
      </div>
    </div>
  );
}

function defaultCondition(kind: Kind, contactFields: FieldDefinitionDto[], orgFields: FieldDefinitionDto[], audience: AudienceOptions): RuleCondition {
  switch (kind) {
    case 'organization':
      return { kind, field: orgFields[0]?.key ?? 'name', op: 'contains', value: '' };
    case 'tag':
      return { kind, op: 'any_of', tagIds: audience.tags?.[0] ? [audience.tags[0].id] : [] };
    case 'list':
      return { kind, op: 'in_any', listIds: audience.lists?.[0] ? [audience.lists[0].id] : [] };
    case 'segment':
      return { kind, op: 'in_any', segmentIds: audience.segments?.[0] ? [audience.segments[0].id] : [] };
    case 'deal':
      return { kind, op: 'has_any_deal', pipelineId: audience.pipelines?.[0]?.id };
    case 'engagement':
      return { kind, event: 'opened', op: 'within_days', days: 30 };
    default: {
      const first = contactFields.find((f) => f.key === 'email') ?? contactFields[0];
      return { kind: 'field', field: first?.key ?? 'email', op: 'contains', value: '' };
    }
  }
}

function ConditionEditor({ c, onChange, contactFields, organizationFields, audience }: { c: RuleCondition; onChange: (c: RuleCondition) => void; contactFields: FieldDefinitionDto[]; organizationFields: FieldDefinitionDto[]; audience: AudienceOptions }) {
  switch (c.kind) {
    case 'field':
    case 'organization': {
      const defs = c.kind === 'organization' ? organizationFields : contactFields;
      const def = defs.find((d) => d.key === c.field);
      const vk = valueKind(def);
      const ops = OPS[vk];
      const op = ops.some((o) => o.value === c.op) ? c.op : (ops[0].value as typeof c.op);
      return (
        <>
          <Select
            {...sel}
            showSearch
            optionFilterProp="label"
            value={c.field}
            style={{ width: 180 }}
            onChange={(field) => onChange({ ...c, field, op: OPS[valueKind(defs.find((d) => d.key === field))][0].value as typeof c.op, value: undefined })}
            options={defs.map((d) => ({ value: d.key, label: d.label }))}
          />
          <Select size="small" value={op} style={{ width: 170 }} onChange={(o) => onChange({ ...c, op: o as typeof c.op, value: undefined })} options={ops} />
          <ValueInput def={def} kind={vk} op={op} value={c.value} onChange={(v) => onChange({ ...c, value: v as typeof c.value })} />
        </>
      );
    }
    case 'tag':
      return (
        <>
          <Select size="small" value={c.op} style={{ width: 130 }} onChange={(op) => onChange({ ...c, op })} options={[{ value: 'any_of', label: 'has any of' }, { value: 'all_of', label: 'has all of' }, { value: 'none_of', label: 'has none of' }]} />
          <Select {...sel} mode="multiple" optionFilterProp="label" value={c.tagIds} onChange={(tagIds) => onChange({ ...c, tagIds })} options={(audience.tags ?? []).map((t) => ({ value: t.id, label: t.name }))} style={{ minWidth: 220 }} />
        </>
      );
    case 'list':
      return (
        <>
          <Select size="small" value={c.op} style={{ width: 130 }} onChange={(op) => onChange({ ...c, op })} options={[{ value: 'in_any', label: 'is in any of' }, { value: 'not_in_any', label: 'is not in' }]} />
          <Select {...sel} mode="multiple" optionFilterProp="label" value={c.listIds} onChange={(listIds) => onChange({ ...c, listIds })} options={(audience.lists ?? []).map((l) => ({ value: l.id, label: l.name }))} style={{ minWidth: 220 }} />
        </>
      );
    case 'segment':
      return (
        <>
          <Select size="small" value={c.op} style={{ width: 130 }} onChange={(op) => onChange({ ...c, op })} options={[{ value: 'in_any', label: 'is in any of' }, { value: 'not_in_any', label: 'is not in' }]} />
          <Select {...sel} mode="multiple" optionFilterProp="label" value={c.segmentIds} onChange={(segmentIds) => onChange({ ...c, segmentIds })} options={(audience.segments ?? []).map((s) => ({ value: s.id, label: s.name }))} style={{ minWidth: 220 }} />
        </>
      );
    case 'deal': {
      const pipeline = (audience.pipelines ?? []).find((p) => p.id === c.pipelineId) ?? audience.pipelines?.[0];
      const stageOps = c.op === 'has_deal_in_stage' || c.op === 'has_no_deal_in_stage';
      return (
        <>
          <Select
            size="small"
            value={c.op}
            style={{ width: 170 }}
            onChange={(op) => onChange({ ...c, op })}
            options={[
              { value: 'has_any_deal', label: 'has a deal' },
              { value: 'has_no_deal', label: 'has no deal' },
              { value: 'has_deal_in_stage', label: 'has a deal in stage' },
              { value: 'has_no_deal_in_stage', label: 'has no deal in stage' },
            ]}
          />
          <Select size="small" value={pipeline?.id} style={{ width: 150 }} onChange={(pipelineId) => onChange({ ...c, pipelineId, stageIds: [] })} options={(audience.pipelines ?? []).map((p) => ({ value: p.id, label: p.name }))} />
          {stageOps && (
            <Select {...sel} mode="multiple" optionFilterProp="label" value={c.stageIds ?? []} onChange={(stageIds) => onChange({ ...c, stageIds })} options={(pipeline?.stages ?? []).map((s) => ({ value: s.id, label: s.name }))} style={{ minWidth: 200 }} />
          )}
        </>
      );
    }
    case 'engagement': {
      const needsDays = c.op === 'within_days' || c.op === 'not_within_days';
      return (
        <>
          <Select
            size="small"
            value={c.event}
            style={{ width: 150 }}
            onChange={(event) => onChange({ ...c, event })}
            options={[
              { value: 'received_email', label: 'was sent an email' },
              { value: 'opened', label: 'opened an email' },
              { value: 'clicked', label: 'clicked a link' },
              { value: 'replied', label: 'replied' },
              { value: 'bounced', label: 'bounced' },
            ]}
          />
          <Select size="small" value={c.op} style={{ width: 150 }} onChange={(op) => onChange({ ...c, op })} options={[{ value: 'within_days', label: 'in the last' }, { value: 'not_within_days', label: 'not in the last' }, { value: 'ever', label: 'ever' }, { value: 'never', label: 'never' }]} />
          {needsDays && <InputNumber size="small" min={1} value={c.days} onChange={(days) => onChange({ ...c, days: days ?? undefined })} addonAfter="days" style={{ width: 130 }} />}
        </>
      );
    }
    default:
      return null;
  }
}

function ValueInput({ def, kind, op, value, onChange }: { def?: FieldDefinitionDto; kind: ValueKind; op: string; value: unknown; onChange: (v: unknown) => void }) {
  if (op === 'is_empty' || op === 'is_not_empty') return null;
  const options = ((def?.options ?? []) as FieldOption[]).map((o) => ({ value: o.value, label: o.label }));
  if (kind === 'boolean') {
    return <Select size="small" style={{ width: 100 }} value={value === true || value === 'true' ? 'true' : 'false'} onChange={(v) => onChange(v === 'true')} options={[{ value: 'true', label: 'Yes' }, { value: 'false', label: 'No' }]} />;
  }
  if (kind === 'select' || kind === 'multiselect') {
    const multi = op === 'in' || op === 'not_in' || op === 'contains' || op === 'not_contains';
    // free-form lists have no options to pick from: type the values instead
    const mode = multi ? (options.length ? 'multiple' : 'tags') : undefined;
    return <Select size="small" mode={mode} style={{ minWidth: 180 }} value={(value as string | string[] | undefined) ?? (multi ? [] : undefined)} onChange={(v) => onChange(v)} options={options} tokenSeparators={mode === 'tags' ? [','] : undefined} />;
  }
  if (kind === 'number' || op === 'within_days' || op === 'not_within_days') {
    return <InputNumber size="small" value={value as number | undefined} onChange={(v) => onChange(v)} style={{ width: 120 }} placeholder={op.includes('days') ? 'days' : undefined} />;
  }
  if (kind === 'date') {
    return <DatePicker size="small" value={value ? dayjs(value as string) : undefined} onChange={(d) => onChange(d ? d.toISOString() : undefined)} />;
  }
  if (op === 'in' || op === 'not_in') {
    return <Select size="small" mode="tags" style={{ minWidth: 200 }} value={(value as string[] | undefined) ?? []} onChange={(v) => onChange(v)} placeholder="type values" tokenSeparators={[',']} />;
  }
  return <Input size="small" value={(value as string | undefined) ?? ''} onChange={(e) => onChange(e.target.value)} style={{ width: 180 }} />;
}

const TAG_OP_LABEL: Record<string, string> = { any_of: 'has any of', all_of: 'has all of', none_of: 'has none of' };
const DEAL_OP_LABEL: Record<string, string> = { has_any_deal: 'Has a deal', has_no_deal: 'Has no deal', has_deal_in_stage: 'Has a deal in stage', has_no_deal_in_stage: 'Has no deal in stage' };
const EVENT_LABEL: Record<string, string> = { received_email: 'Was sent an email', opened: 'Opened an email', clicked: 'Clicked a link', replied: 'Replied', bounced: 'Bounced' };
const ENGAGEMENT_OP_LABEL: Record<string, string> = { within_days: 'in the last', not_within_days: 'not in the last', ever: 'ever', never: 'never' };

/** Compact description of active rules for toolbars. */
export function FilterSummary({ group, fields, audience = {} }: { group?: RuleGroup; fields: FieldDefinitionDto[]; audience?: AudienceOptions }) {
  if (!group?.conditions.length) return null;
  const name = (xs: { id: string; name: string }[] | undefined, ids: string[]) => ids.map((id) => xs?.find((x) => x.id === id)?.name ?? '?').join(', ');
  return (
    <Space size={4} wrap>
      {group.conditions.map((c, i) => {
        if (!('kind' in c)) return null;
        let text = '';
        switch (c.kind) {
          case 'field':
          case 'organization': {
            const def = fields.find((f) => f.key === c.field);
            const vk = valueKind(def);
            const opLabel = OPS[vk].find((o) => o.value === c.op)?.label ?? c.op.replace(/_/g, ' ');
            const optLabel = (v: unknown) => ((def?.options ?? []) as FieldOption[]).find((o) => o.value === v)?.label ?? String(v);
            const opText = c.op === 'within_days' ? 'in the last' : c.op === 'not_within_days' ? 'not in the last' : opLabel;
            let v = '';
            if (c.op === 'is_empty' || c.op === 'is_not_empty') v = '';
            else if (c.op === 'within_days' || c.op === 'not_within_days') v = `${c.value ?? ''} days`;
            else if (vk === 'date') v = c.value ? fmtDate(c.value as string) : '';
            else if (vk === 'boolean') v = c.value === true || c.value === 'true' ? 'Yes' : 'No';
            else if (Array.isArray(c.value)) v = c.value.map(optLabel).join(', ');
            else if (c.value !== undefined && c.value !== null) v = vk === 'select' ? optLabel(c.value) : String(c.value);
            text = `${c.kind === 'organization' ? 'Organization ' : ''}${def?.label ?? c.field} ${opText}${v ? ` ${v}` : ''}`;
            break;
          }
          case 'tag':
            text = `Tag ${TAG_OP_LABEL[c.op] ?? c.op} ${name(audience.tags, c.tagIds)}`;
            break;
          case 'list':
            text = `${c.op === 'in_any' ? 'In list' : 'Not in list'} ${name(audience.lists, c.listIds)}`;
            break;
          case 'segment':
            text = `${c.op === 'in_any' ? 'In segment' : 'Not in segment'} ${name(audience.segments, c.segmentIds)}`;
            break;
          case 'deal':
            text = DEAL_OP_LABEL[c.op] ?? `Deal: ${c.op.replace(/_/g, ' ')}`;
            break;
          case 'engagement':
            text = `${EVENT_LABEL[c.event] ?? c.event} ${ENGAGEMENT_OP_LABEL[c.op] ?? c.op}${c.days && (c.op === 'within_days' || c.op === 'not_within_days') ? ` ${c.days} days` : ''}`;
            break;
        }
        return <Tag key={i}>{text}</Tag>;
      })}
    </Space>
  );
}
